// react
import { FC } from 'react';

// mui
import { Grid, Paper, Skeleton, Stack } from '@mui/material';

// design system
import { spacing } from '../../../utils/designSystem';

// Types

export type PageDetailSkeletonVariant =
	| 'activity'
	| 'condition'
	| 'contract'
	| 'workPlan'
	| 'voucher';

export interface PageDetailSkeletonProps {
	/** Number of cards to show in the left column (default: 2) */
	leftCards?: number;
	/** Number of cards to show in the right column (default: 2) */
	rightCards?: number;
	/** @deprecated No longer used - header skeleton is now in HeaderDetailPage */
	variant?: PageDetailSkeletonVariant;
}

// Helper Components

const SkeletonCard: FC<{ lines?: number }> = ({ lines = 3 }) => (
	<Paper
		elevation={0}
		sx={{
			border: '1px solid',
			borderColor: 'divider',
			borderRadius: 1.5,
			p: spacing.sm,
		}}>
		<Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
		{Array.from({ length: lines }).map((_, i) => (
			<Skeleton
				key={i}
				variant="text"
				width={i === lines - 1 ? '60%' : '100%'}
				height={18}
				sx={{ mb: 0.5 }}
			/>
		))}
	</Paper>
);

// Component

/**
 * PageDetailSkeleton - Reusable loading skeleton for detail pages
 *
 * Displays a skeleton UI matching the layout of detail pages while data is loading.
 * Includes a header section with gradient background and a two-column layout.
 */
export const PageDetailSkeleton: FC<PageDetailSkeletonProps> = ({
	leftCards = 2,
	rightCards = 2,
}) => {
	return (
		<Grid container spacing={{ xs: spacing.md, md: spacing.lg }}>
			{/* Left Column */}
			<Grid item xs={12} lg={8}>
				<Stack spacing={spacing.lg}>
					{Array.from({ length: leftCards }).map((_, i) => (
						<SkeletonCard key={i} lines={i === 0 ? 4 : 3} />
					))}
				</Stack>
			</Grid>

			{/* Right Column */}
			<Grid item xs={12} lg={4}>
				<Stack spacing={spacing.lg}>
					{Array.from({ length: rightCards }).map((_, i) => (
						<SkeletonCard key={i} lines={3} />
					))}
				</Stack>
			</Grid>
		</Grid>
	);
};
